import { getMediaUrl, getThumbnailUrl } from './media';
/**
 * Get the hero image URLs for a venue
 */
export const getVenueHeroImageUrls = (venue, heroMedia, cloudName, transform) => {
    if (!venue.heroImageIds.length || !heroMedia.length) {
        return [];
    }
    // Filter and sort hero media to match the order in venue.heroImageIds
    const sortedMedia = venue.heroImageIds
        .map((id) => heroMedia.find(media => media.id === id))
        .filter((media) => media !== undefined);
    return sortedMedia.map((media) => getMediaUrl(media.cloudinaryPublicId, cloudName, 'cloudinary', transform));
};
/**
 * Get the first hero image URL for a venue (for backward compatibility)
 */
export const getVenueHeroImageUrl = (venue, heroMedia, cloudName, transform) => {
    if (!venue.heroImageIds.length || !heroMedia) {
        return null;
    }
    return getMediaUrl(heroMedia.cloudinaryPublicId, cloudName, 'cloudinary', transform);
};
/**
 * Get gallery image URLs for a venue
 */
export const getVenueGalleryImageUrls = (venue, galleryMedia, cloudName, transform) => {
    if (!venue.imageIds.length) {
        return [];
    }
    // Filter and sort gallery media to match the order in venue.imageIds
    const sortedMedia = venue.imageIds
        .map((id) => galleryMedia.find(media => media.id === id))
        .filter((media) => media !== undefined);
    return sortedMedia.map((media) => getMediaUrl(media.cloudinaryPublicId, cloudName, 'cloudinary', transform));
};
/**
 * Get thumbnail URLs for venue gallery
 */
export const getVenueGalleryThumbnails = (venue, galleryMedia, cloudName, size = 200) => {
    if (!venue.imageIds.length) {
        return [];
    }
    const sortedMedia = venue.imageIds
        .map((id) => galleryMedia.find(media => media.id === id))
        .filter((media) => media !== undefined);
    return sortedMedia.map((media) => getThumbnailUrl(media.cloudinaryPublicId, cloudName, 'cloudinary', size));
};
/**
 * Check if a venue has hero images
 */
export const hasHeroImage = (venue) => {
    return Boolean(venue.heroImageIds.length);
};
/**
 * Check if a venue has multiple hero images
 */
export const hasMultipleHeroImages = (venue) => {
    return venue.heroImageIds.length > 1;
};
/**
 * Get the count of hero images for a venue
 */
export const getHeroImageCount = (venue) => {
    return venue.heroImageIds.length;
};
/**
 * Check if a venue has gallery images
 */
export const hasGalleryImages = (venue) => {
    return Boolean(venue.imageIds.length);
};
/**
 * Get the count of gallery images for a venue
 */
export const getGalleryImageCount = (venue) => {
    return venue.imageIds.length;
};
/**
 * Check if a media item is one of the venue's hero images
 */
export const isVenueHeroImage = (venue, mediaId) => {
    return venue.heroImageIds.includes(mediaId);
};
/**
 * Get the position of a media item in the venue's hero images
 */
export const getHeroImagePosition = (venue, mediaId) => {
    return venue.heroImageIds.indexOf(mediaId);
};
/**
 * Check if a media item is in the venue's gallery
 */
export const isInVenueGallery = (venue, mediaId) => {
    return venue.imageIds.includes(mediaId);
};
/**
 * Get the position of a media item in the venue's gallery
 */
export const getGalleryPosition = (venue, mediaId) => {
    return venue.imageIds.indexOf(mediaId);
};
/**
 * Format venue address for display
 */
export const formatVenueAddress = (venue, includeCountry = false) => {
    const { street, city, state, zipCode, country } = venue.address;
    let address = `${street}, ${city}, ${state} ${zipCode}`;
    if (includeCountry && country !== 'USA') {
        address += `, ${country}`;
    }
    return address;
};
/**
 * Get venue coordinates for mapping
 */
export const getVenueCoordinates = (venue) => {
    return venue.address.coordinates ?? null;
};
/**
 * Check if venue has operating hours
 */
export const hasOperatingHours = (venue) => {
    return Boolean(venue.operatingHours && Object.keys(venue.operatingHours).length > 0);
};
/**
 * Get operating hours for a specific day
 */
export const getOperatingHoursForDay = (venue, day) => {
    if (!venue.operatingHours?.[day]) {
        return null;
    }
    return venue.operatingHours[day];
};
/**
 * Check if venue is open on a specific day
 */
export const isVenueOpenOnDay = (venue, day) => {
    const hours = getOperatingHoursForDay(venue, day);
    return Boolean(hours && !hours.closed);
};
/**
 * Get venue display name (fallback to name if no display name)
 */
export const getVenueDisplayName = (venue) => {
    return venue.name;
};
/**
 * Check if user is a venue admin
 */
export const isVenueAdmin = (venue, userId) => {
    return Boolean(venue.venueAdmins?.includes(userId));
};
/**
 * Get venue admin count
 */
export const getVenueAdminCount = (venue) => {
    return venue.venueAdmins ? venue.venueAdmins.length : 0;
};
/**
 * Check if user can edit a venue based on their role and venue admin status
 */
export const canEditVenue = (venue, userRole, userId) => {
    // SITE_ADMIN and SUPER_ADMIN can edit any venue
    if (userRole === 'SITE_ADMIN' || userRole === 'SUPER_ADMIN') {
        return true;
    }
    // VENUE_ADMIN can edit venues they are assigned to
    if (userRole === 'VENUE_ADMIN') {
        return isVenueAdmin(venue, userId);
    }
    return false;
};
/**
 * Check if user can manage a venue (alias for canEditVenue for management dashboard)
 */
export const canManageVenue = (venue, userRole, userId) => {
    return canEditVenue(venue, userRole, userId);
};
